import React, { useEffect, useState, useCallback, useRef } from "react";
import "../css/components/SearchModal.scss";
import Fuse from "fuse.js";
import { connect } from "react-redux";
import { syllabus } from "../piperAcademy";
import { store } from "../store/configureStore";
import history from "../history";
import { useHotkeys } from "react-hotkeys-hook";
import { TRACKING_EVENT_NAMES } from "../client-config";
import { ButtonIcon } from "../components/_commons/ButtonIcon";
import { getLoggedInBizId, lS, trackEvent } from "../atlas-utils";
import { debounce } from "lodash";
import { ActionTypes } from "../actions/_types";
import { HorizonTour } from "@urbanpiper-engineering/horizon";

const flattenItemsIntoCategory = (items, category) => {
	let flattened = [];
	items.forEach((item) => {
		if (item.render === true && !item.isLocked) {
			if (item.path) {
				flattened.push({
					id: item.key + "-" + item.label,
					title: item.label,
					path: item.path,
					onClick: item.onClick,
					parentLabel: null,
					type: category
				});
			}
			if (item.childrenProps) {
				const children = item.childrenProps
					.filter((child) => child.render !== false)
					.map((child) => ({
						id: child.key + "-" + item.label,
						title: typeof child.label === "string" ? child.label : getStringFromJSX(child.label),
						path: child.path,
						onClick: child.onClick,
						parentLabel: category == PIP_ITEM_TYPES.NAV && item.label == "Analytics" ? null : item.label,
						type:
							category == PIP_ITEM_TYPES.NAV && item.label == "Analytics"
								? PIP_ITEM_TYPES.ANALYTICS
								: category
					}));
				flattened = [...flattened, ...children];
			}
		}
	});
	return flattened;
};

function getStringFromJSX(jsx) {
	if (typeof jsx === "string") return jsx;
	if (React.isValidElement(jsx)) {
		// Handle case where jsx is a div/span with children array
		if (jsx.props && jsx.props.children) {
			if (Array.isArray(jsx.props.children)) {
				// Find the span with text content
				const textSpan = jsx.props.children.find(
					(child) =>
						React.isValidElement(child) && child.type === "span" && typeof child.props.children === "string"
				);
				if (textSpan) {
					return textSpan.props.children;
				}
				// If no direct text span found, process all children
				return jsx.props.children.map((child) => getStringFromJSX(child)).join("");
			}
			// Handle single child
			return getStringFromJSX(jsx.props.children);
		}
		// Fallback to basic properties if no children
		return jsx.props?.label || jsx.props?.title || "";
	}
	return String(jsx || "");
}

function sortItems(items) {
	const TYPE_ORDER = { "Go To": 1, Analytics: 2, Create: 3, Help: 4 };
	return items.sort((a, b) => {
		// First: sort by parentLabel
		if (a.parentLabel !== b.parentLabel) {
			return (a.parentLabel || "").localeCompare(b.parentLabel || "");
		}

		// Second: sort by type priority
		const typeCompare = TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
		if (typeCompare !== 0) return typeCompare;

		// Third: sort alphabetically by title
		return a.title.localeCompare(b.title);
	});
}

const fuse = new Fuse([], {
	keys: ["combined", "parentLabel", "title"],
	threshold: 0.4,
	includeScore: true,
	useExtendedSearch: true
});

const MODES = {
	SEARCH: "search",
	CHAT: "chat"
};

const PIP_ITEM_TYPES = {
	NAV: "Go To",
	HELP: "Help",
	CREATE: "Create",
	ANALYTICS: "Analytics",
	AI: "AI"
};

// const MAGIC_ITEMS = {
// 	help: {
// 		id: "magic-help",
// 		title: "Help documentation",
// 		isMagical: true,
// 		type: "help"
// 	},
// 	analytics: {
// 		id: "magic-analytics",
// 		title: "Analytics",
// 		isMagical: true,
// 		type: "analytics"
// 	},
// 	create: {
// 		id: "magic-create",
// 		title: "Create...",
// 		isMagical: true,
// 		type: "create"
// 	},
// 	how: {
// 		id: "magic-how",
// 		title: "How to guides...",
// 		isMagical: true,
// 		type: "help"
// 	}
// };

function PipSearch({ navItems, createFlows, pipSearchState }) {
	const { open } = pipSearchState;
	const [query, setQuery] = useState("");
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const [filteredResults, setFilteredResults] = useState([]);
	const [dataset, setDataset] = useState([]);
	const [flatResults, setFlatResults] = useState([]);
	const [mode, setMode] = useState(MODES.SEARCH);
	const [chatHistory, setChatHistory] = useState([]);
	const storedHistory = lS.get("pipSearchHistory") || {}; // default to empty object
	const bizId = getLoggedInBizId();
	const [historyItems, setHistoryItems] = useState(storedHistory[bizId] || []);
	const [isLoading, setIsLoading] = useState(false);
	const storedFirstInteraction = lS.get("pipFirstInteractionDone") || {};
	const [isFirstInteraction, setIsFirstInteraction] = useState(!(storedFirstInteraction[bizId] || false));
	const [placeholder, setPlaceholder] = useState("");
	const [isDeleting, setIsDeleting] = useState(false);
	const [phraseIndex, setPhraseIndex] = useState(0);
	const [charIndex, setCharIndex] = useState(0);
	const inputRef = useRef(null);
	const inputFocusedRef = useRef(false);
	const modalRef = useRef(null);
	const voiceSearchRef = useRef(null);
	const resultsRef = useRef(null);
	const introRef = useRef(null);

	const phrases = [
		"Try “Orders” or “Items”",
		"Search “Customers” or “Analytics”",
		"Need to add something? Try “Create Item”",
		"Looking for help? Try “Payouts” or “Billing”",
		"Go to “Catalogue” or “Locations”",
		"Try “User Invite” or “API Access”",
		"Start with “Create Category”",
		"Search “Webhooks” or “Schedules”",
		"Explore “Periscope” or “Reports”",
		"Need setup? Try “Create Brand”",
		"Type “Store Toggle” or “Feedback”",
		"Search “Intro to Catalogue”",
		"Try “Edit Tax” or “New Tag Group”",
		"Jump to “Campaigns” or “Segments”",
		"Need help? Try “Food Prep Time”",
		"Search for any module or try “Go to”"
	];

	const handlePipSearchState = (state = false) => {
		store.dispatch({
			type: ActionTypes.UPDATE_PIP_SEARCH_STATE,
			payload: { open: state }
		});
	};

	useEffect(() => {
		if (highlightedIndex >= flatResults.length) {
			setHighlightedIndex(0);
		}
	}, [flatResults, highlightedIndex]);

	useEffect(() => {
		const currentPhrase = phrases[phraseIndex];
		let timer;

		if (isDeleting) {
			timer = setTimeout(() => {
				setPlaceholder(currentPhrase.substring(0, charIndex - 1));
				setCharIndex(charIndex - 1);
			}, 50);
		} else {
			timer = setTimeout(() => {
				setPlaceholder(currentPhrase.substring(0, charIndex + 1));
				setCharIndex(charIndex + 1);
			}, 100);
		}

		if (!isDeleting && charIndex === currentPhrase.length) {
			setTimeout(() => setIsDeleting(true), 1500);
		}

		if (isDeleting && charIndex === 0) {
			setIsDeleting(false);
			setPhraseIndex((prev) => (prev + 1) % phrases.length);
		}

		return () => clearTimeout(timer);
	}, [charIndex, isDeleting, phraseIndex]);

	const scrollSelectedIntoView = (index) => {
		setTimeout(() => {
			const element = document.getElementById(`search-result-${index}`);
			if (element) {
				element.scrollIntoView({
					block: index === 0 ? "center" : "nearest",
					behavior: "instant",
					inline: "nearest"
				});
			}
		}, 0);
	};

	const scrollSelectedIntoViewOnHover = (index) => {
		return;
		// if (!open || flatResults.length === 0) return;
		// setHighlightedIndex(index);
		// scrollSelectedIntoView(index);
	};

	const maintainInputFocus = () => {
		setTimeout(() => {
			if (open && inputRef.current) {
				inputRef.current.focus();
			}
		}, 0);
	};

	useHotkeys(
		"meta+k,cmd+k,ctrl+k",
		(e) => {
			e.preventDefault();
			handlePipSearchState(!open);
			if (!open) {
				trackEvent(TRACKING_EVENT_NAMES.PIP_OPEN, { src: "keys" });
				setQuery("");
				setTimeout(() => inputRef.current?.focus(), 0);
			}
		},
		{ enableOnFormTags: ["INPUT", "input"] },
		[open]
	);

	// useHotkeys(
	// 	"meta+i,cmd+i,ctrl+i",
	// 	(e) => {
	// 		e.preventDefault();
	// 		handlePipSearchState(true);
	// 		setQuery("");
	// 		setTimeout(() => inputRef.current?.focus(), 0);
	// 		if (voiceSearchRef.current) voiceSearchRef.current.startListening();
	// 	},
	// 	{ enableOnFormTags: ["INPUT", "input"] },
	// 	[open]
	// );

	useHotkeys(
		"esc",
		(e) => {
			e.preventDefault();
			if (open && !!query) {
				setQuery("");
				setHighlightedIndex(0);
				// setTimeout(() => inputRef.current?.blur(), 0);
			} else {
				handlePipSearchState(false);
				setQuery("");
				setChatHistory([]);
				setMode(MODES.SEARCH);
			}
			// if (voiceSearchRef.current) voiceSearchRef.current.stopListening();
		},
		{ enableOnFormTags: true, enabled: open },
		[open, query]
	);

	useHotkeys(
		"up,shift+tab",
		(e) => {
			if (open) {
				e.preventDefault();
				const prevIndex = highlightedIndex === 0 ? flatResults.length - 1 : highlightedIndex - 1;
				setHighlightedIndex(prevIndex);
				if (prevIndex == 0) scrollSelectedIntoView(prevIndex);
				else if (prevIndex == flatResults.length - 1) scrollSelectedIntoView(prevIndex);
				else scrollSelectedIntoView(prevIndex - 1);
			}
		},
		{ enableOnFormTags: ["INPUT", "input"], enabled: open },
		[open, flatResults, highlightedIndex]
	);

	useHotkeys(
		"/",
		(e) => {
			e.preventDefault();
			if (!open) {
				handlePipSearchState(true);
			}
			setTimeout(() => inputFocusedRef.current?.focus(), 0);
		},
		{ enableOnFormTags: true, enabled: open },
		[open]
	);

	useHotkeys(
		"down,tab",
		(e) => {
			if (open) {
				e.preventDefault();
				const nextIndex = (highlightedIndex + 1) % flatResults.length;
				setHighlightedIndex(nextIndex);
				scrollSelectedIntoView(nextIndex);
			}
		},
		{ enableOnFormTags: ["INPUT", "input"], enabled: open },
		[open, flatResults, highlightedIndex]
	);

	useHotkeys(
		"enter",
		async (e) => {
			if (open) {
				e.preventDefault();
				if (mode === MODES.CHAT) {
					handleChatSubmit();
					return;
				}

				// Determine which item is selected based on highlightedIndex
				let selectedItem = null;
				const historyLength = query === "" ? historyItems?.length || 0 : 0;

				if (highlightedIndex < historyLength) {
					// Selected item is from history
					selectedItem = historyItems[highlightedIndex];
				} else if (filteredResults && filteredResults.length > 0) {
					// Selected item is from filtered results
					const filteredIndex = highlightedIndex - historyLength;
					selectedItem = filteredResults[filteredIndex];
				}

				if (selectedItem) {
					handleItemClick(selectedItem);
					trackEvent(TRACKING_EVENT_NAMES.PIP_NAVIGATE, {
						item: selectedItem.title,
						category: selectedItem.parentLabel || "Other"
					});
				}
			}
		},
		{ enableOnFormTags: ["INPUT", "input"], enabled: open },
		[open, filteredResults, highlightedIndex, mode, query, chatHistory, historyItems]
	);

	const handleQuery = async (query) => {
		try {
			setIsLoading(true);

			// First, try the local backend
			let backendData;
			try {
				const backendResp = await fetch("http://localhost:8000/chat", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"Access-Control-Allow-Origin": "*"
					},
					body: JSON.stringify({ query })
				});

				if (!backendResp.ok) {
					throw new Error(`Backend server error: ${backendResp.status}`);
				}

				backendData = await backendResp.json();
			} catch (backendError) {
				// Fallback responses based on query type
				if (query.toLowerCase().startsWith("pip:")) {
					return "😅 I can't analyze data right now as I'm unable to connect to the analysis server. Please try again in a few minutes or contact support if the issue persists.";
				} else if (query.toLowerCase().startsWith("ask:")) {
					return "I can try to help you with general questions even though I'm offline. What would you like to know?";
				} else {
					return "🔌 I'm currently operating in offline mode with limited capabilities. I'll do my best to help, but some features might not work as expected.";
				}
			}

			// Try Gemini API only if we have backend data
			if (backendData?.response) {
				try {
					const geminiResp = await fetch(
						"https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent",
						{
							method: "POST",
							headers: {
								"Content-Type": "application/json",
								"x-goog-api-key": process.env.REACT_APP_GEMINI_API_KEY
							},
							body: JSON.stringify({
								contents: [
									{
										role: "user",
										parts: [
											{
												text: `Make this response more user friendly and conversational:
                                User query: ${query}
                                Original response: ${backendData.response}
                                Please provide a clear and concise response.`
											}
										]
									}
								]
							})
						}
					);

					if (!geminiResp.ok) {
						return backendData.response; // Fallback to original response
					}

					const geminiData = await geminiResp.json();
					return geminiData.candidates[0].content.parts[0].text;
				} catch (geminiError) {
					return (
						backendData.response ||
						"I couldn't process that in a conversational way, but here's what I know."
					);
				}
			}
		} catch (error) {
			// Return user-friendly error messages based on error type
			if (error.message.includes("Cannot connect to local server")) {
				return "😕 I'm having trouble connecting to the server. Please make sure the local server is running and try again.";
			} else if (error.message.includes("Gemini API error")) {
				return "🤔 I'm having trouble making my response more conversational. Let me try a simpler response.";
			} else {
				return "😅 Sorry, I encountered an error. Please try rephrasing your question or try again later.";
			}
		} finally {
			setIsLoading(false);
			maintainInputFocus();
		}
	};

	useEffect(() => {
		setQuery("");
		setHighlightedIndex(0);
		scrollSelectedIntoView(0);
		if (voiceSearchRef.current) voiceSearchRef.current.stopListening();
	}, [open]);

	useEffect(() => {
		if (navItems) {
			const flattenedItems = flattenItemsIntoCategory(navItems, PIP_ITEM_TYPES.NAV);
			const flattenedCreateFlows = flattenItemsIntoCategory(createFlows, PIP_ITEM_TYPES.CREATE);

			// Restructure helpResults to match the format expected by flattenItemsIntoCategory
			const helpResults = syllabus.courses
				.map((course) => {
					const matchingLessons = syllabus.lessons.filter((lesson) => lesson.courses.includes(course.code));
					if (matchingLessons.length === 0) return null;

					return {
						key: course.code,
						label: course.title,
						render: true,
						isLocked: false,
						childrenProps: matchingLessons.map((lesson) => ({
							key: lesson.id,
							label: lesson.title,
							path: `/piper-academy/${lesson.id}`,
							render: true,
							isLocked: false
						}))
					};
				})
				.filter(Boolean);

			// Flatten the help results
			const flattenedHelpFlows = flattenItemsIntoCategory(helpResults, PIP_ITEM_TYPES.HELP);

			const dataSet = sortItems([...flattenedItems, ...flattenedCreateFlows, ...flattenedHelpFlows]);

			setDataset(dataSet);
			fuse.setCollection(dataSet);
		}
	}, [navItems]);

	useEffect(() => {
		const queryLower = query.toLowerCase().trim();

		// Show default items when query is empty
		if (!queryLower && mode !== MODES.CHAT) {
			// Add history items to the beginning of flatResults when showing default view
			const defaultResults = [
				// ...(historyItems || []), // Add history items at the start
				...dataset
			];

			setFilteredResults(defaultResults);
			setFlatResults(defaultResults);
			setHighlightedIndex(0);
			setMode(MODES.SEARCH);
			return;
		}

		// Get relevant magic items based on query
		let magicResults = [];
		// if (queryLower.includes("pip")) magicResults.push(MAGIC_ITEMS.pip);
		// if (queryLower.includes("ask")) magicResults.push(MAGIC_ITEMS.ask);
		// if (queryLower.includes("help")) magicResults.push(MAGIC_ITEMS.help);
		// else if (queryLower.includes("how")) magicResults.push(MAGIC_ITEMS.how);
		// else if (queryLower.includes("anl") || queryLower.includes("analytics"))
		// 	magicResults.push(MAGIC_ITEMS.analytics);
		// // else if (queryLower.includes("create")) magicResults.push(MAGIC_ITEMS.create);
		// else {
		// 	magicResults = [];
		// 	setMode(MODES.SEARCH);
		// }

		// Handle analytics
		// if (queryLower.startsWith("anl") || queryLower.startsWith("analytics")) {
		// 	const analyticsQuery = queryLower.replace(/^(anl:?|analytics:?)/, "").trim();
		// 	const analyticsResults = Object.keys(ANALYTICS_SECTIONS)
		// 		.filter((section) => !ANALYTICS_SECTIONS[section].hidden)
		// 		.map((section) => {
		// 			const matchingResults = ANALYTICS_SECTIONS[section].tabs.filter((tab) =>
		// 				tab.label.toLowerCase().includes(analyticsQuery)
		// 			);
		// 			if (matchingResults.length === 0) return null;
		// 			return {
		// 				id: ANALYTICS_SECTIONS[section].topic,
		// 				title: `${ANALYTICS_SECTIONS[section].topic}`,
		// 				children: matchingResults.map((tab) => ({
		// 					id: tab.value,
		// 					title: `${tab.label}`,
		// 					path: tab.link
		// 				}))
		// 			};
		// 		})
		// 		.filter(Boolean);
		// 	setFilteredResults(analyticsResults);
		// 	setFlatResults(analyticsResults.flatMap((g) => g.children));
		// 	setMode(MODES.ANALYTICS);
		// 	return;
		// }

		// Handle create flows
		// if (queryLower.startsWith("create")) {
		// 	const createFlowsQuery = queryLower.replace(/^create:?/, "").trim();
		// 	const createFlowsResults = createFlows
		// 		.map((flow) => {
		// 			const matchingResults = flow.childrenProps.filter(
		// 				(f) => f.render && f.label.toLowerCase().includes(createFlowsQuery)
		// 			);
		// 			if (matchingResults.length === 0) return null;
		// 			return {
		// 				id: flow.key,
		// 				title: flow.label,
		// 				children: matchingResults.map((f) => ({
		// 					id: f.key,
		// 					title: `${f.label}`,
		// 					path: f.path
		// 				}))
		// 			};
		// 		})
		// 		.filter(Boolean);
		// 	setFilteredResults(createFlowsResults);
		// 	setFlatResults(createFlowsResults.flatMap((g) => g.children));
		// 	setMode(MODES.CREATE);
		// 	return;
		// }

		// Handle help mode
		// if (queryLower.startsWith("help") || queryLower.startsWith("how to")) {
		// 	const helpQuery = queryLower.replace(/^(help:?|how to:?)/, "").trim();
		// 	const helpResults = syllabus.courses
		// 		.map((course) => {
		// 			const matchingLessons = syllabus.lessons.filter(
		// 				(lesson) =>
		// 					lesson.courses.includes(course.code) &&
		// 					(lesson.title.toLowerCase().includes(helpQuery) ||
		// 						lesson.searchKeywords?.some((kw) => kw.includes(helpQuery)))
		// 			);
		// 			if (matchingLessons.length === 0) return null;
		// 			return {
		// 				id: course.code,
		// 				title: `${course.title}`,
		// 				children: matchingLessons.map((lesson) => ({
		// 					id: lesson.id,
		// 					title: lesson.title,
		// 					path: `/piper-academy/${lesson.id}`,
		// 					parentLabel: course.title
		// 				}))
		// 			};
		// 		})
		// 		.filter(Boolean);
		// 	setFilteredResults(helpResults);
		// 	setFlatResults(helpResults.flatMap((g) => g.children));
		// 	setMode(MODES.HELP);
		// 	return;
		// }

		// Commented for AI based integration
		// if (
		// 	queryLower.startsWith("pip:") ||
		// 	queryLower.startsWith("pip ") ||
		// 	queryLower.startsWith("ask:") ||
		// 	queryLower.startsWith("ask ")
		// ) {
		// 	setMode(MODES.CHAT);
		// } else
		{
			// Create a combined dataset for fuzzy search
			const searchableDataset = [...dataset];

			const enrichedData = searchableDataset.map((item) => ({
				...item,
				combined: `${item.type} ${item.title}`
			}));
			// Update fuse collection with combined dataset
			fuse.setCollection(enrichedData);

			// Step 1: Split into tokens
			const tokens = query.split(" ");

			// Step 2: Fuse search for each token individually
			const fuzzyResults = fuse.search(queryLower);
			// Separate regular and help results
			const searchResults = [];
			const helpResults = [];
			const createFlowsResults = [];

			fuzzyResults.forEach(({ item }) => {
				if (item.isHelp) {
					helpResults.push({
						...item,
						title: `📘 ${item.title}`,
						parentLabel: "Help"
					});
				} else if (item.isCreate) {
					createFlowsResults.push({
						...item,
						title: item.title,
						parentLabel: "Create"
					});
				} else {
					searchResults.push(item);
				}
			});

			// Combine all results in the desired order
			const combinedResults = [...magicResults, ...searchResults, ...createFlowsResults, ...helpResults];

			setFilteredResults(combinedResults);
			setFlatResults(combinedResults);
			setHighlightedIndex(0);
		}
	}, [query, dataset, mode, historyItems]);

	useEffect(() => {
		if (open) {
			maintainInputFocus();
		}
	}, [open, chatHistory, mode]); // Dependencies that might affect focus

	useEffect(() => {
		const inputEl = inputRef.current;
		if (!inputEl) return;

		const handleFocus = () => {
			inputFocusedRef.current = true;
		};

		const handleBlur = () => {
			inputFocusedRef.current = false;
		};

		inputEl.addEventListener("focus", handleFocus);
		inputEl.addEventListener("blur", handleBlur);

		return () => {
			inputEl.removeEventListener("focus", handleFocus);
			inputEl.removeEventListener("blur", handleBlur);
		};
	}, []);

	const handleItemClick = (item) => {
		if (item) {
			const updated = [...historyItems, item].slice(-2); // keep last 2
			setHistoryItems(updated);
			const pipSearchHistory = lS.get("pipSearchHistory") || {};
			lS.set("pipSearchHistory", {
				...pipSearchHistory,
				[bizId]: [...historyItems]
			});

			// Handle magic items
			trackEvent(TRACKING_EVENT_NAMES.PIP_NAVIGATE, {
				item: item.title,
				category: item.isMagical ? "Magic Items" : item.parentLabel || "Other"
			});
		}
		// if (item.isMagical) {
		// 	if (item.type === "chat") {
		// 		setMode(MODES.CHAT);
		// 		// Set appropriate prefix based on magic item
		// 		setQuery(item.id === "magic-pip" ? "pip: " : "ask: ");
		// 		setChatHistory([]);
		// 		maintainInputFocus();
		// 		return;
		// 	} else if (item.type === "help") {
		// 		setMode(MODES.HELP);
		// 		setQuery(""); // Clear the query first
		// 		setTimeout(() => {
		// 			// Set the appropriate prefix after mode change
		// 			setQuery(item.id === "magic-how" ? "how to: " : "help: ");
		// 			maintainInputFocus();
		// 		}, 0);
		// 		return;
		// 	} else if (item.type === "anl" || item.type === "analytics") {
		// 		setMode(MODES.ANALYTICS);
		// 		setQuery("analytics: "); // Clear the query first
		// 		maintainInputFocus();
		// 		return;
		// 	} else if (item.type === "create") {
		// 		setMode(MODES.CREATE);
		// 		setQuery("create: "); // Clear the query first
		// 		maintainInputFocus();
		// 		return;
		// 	}
		// }

		// Check if it's a Piper Academy path and if both item and group exist
		if (item.path?.startsWith("/piper-academy") && item.group?.id) {
			const currCourse = syllabus.courses.find((course) => course.code.includes(item.group.id));
			const currLesson = syllabus.lessons.find((lesson) => lesson.id === item.id);

			if (currCourse && currLesson) {
				store.dispatch({
					type: "UPDATE_PIPER_ACADEMY_STATE",
					payload: {
						currCourse,
						currLesson,
						start: ""
					}
				});
				history.push(item.path);
				handlePipSearchState(false);
				return;
			}
		}

		// Normal case for Piper Academy
		if (item.path?.startsWith("/piper-academy")) {
			const currCourse = syllabus.courses.find((course) =>
				item?.group ? course.code.includes(item?.group?.id) : course.paths.includes(item.id)
			);
			const currLesson = syllabus.lessons.find((lesson) => lesson.id === item?.id);
			store.dispatch({
				type: "UPDATE_PIPER_ACADEMY_STATE",
				payload: {
					currCourse,
					currLesson,
					start: ""
				}
			});
			history.push(item.path);
			handlePipSearchState(false);
			return;
		}

		// Handle regular navigation items

		if (item.onClick) {
			item.onClick();
			return;
		}

		if (item.path) {
			history.push(item.path);
		}
		handlePipSearchState(false);
	};

	const handleOverlayClick = (e) => {
		// Close only if clicking the overlay (not the modal itself)
		if (e.target.classList.contains("search-modal-overlay")) {
			handlePipSearchState(false);
			setQuery("");
			setChatHistory([]);
			setMode(MODES.SEARCH);
		}
	};

	// const handleVoiceSearch = (query) => {
	// 	if (!query) return;
	// 	handlePipSearchState(true);
	// 	setQuery(query);
	// 	setTimeout(() => inputRef.current?.focus(), 0);
	// };

	const handleChatSubmit = async () => {
		if (!query.trim()) return;
		const prompt = query.replace(/^(pip:|pip|ask:|ask)/, "").trim();

		// Add user message to chat history
		const userMessage = {
			type: "user",
			text: prompt
		};
		setChatHistory((prev) => [...prev, userMessage]);

		// Clear input
		setQuery("");

		try {
			// Get AI response
			const response = await handleQuery(userMessage.text);

			// Add AI response to chat history
			setChatHistory((prev) => [
				...prev,
				{
					type: "ai",
					text: response
				}
			]);
		} catch (error) {
			setChatHistory((prev) => [
				...prev,
				{
					type: "ai",
					text: "Sorry, I encountered an error processing your request."
				}
			]);
		}
	};

	const handleWelcomeModalDismiss = () => {
		setIsFirstInteraction(false);
		const pipFirstInteractionDone = lS.get("pipFirstInteractionDone") || {};
		lS.set("pipFirstInteractionDone", {
			...pipFirstInteractionDone,
			[bizId]: true
		});
	};

	const handleQueryInput = (query) => {
		setIsFirstInteraction(false);
		const pipFirstInteractionDone = lS.get("pipFirstInteractionDone") || {};
		lS.set("pipFirstInteractionDone", {
			...pipFirstInteractionDone,
			[bizId]: true
		});
		setQuery(query);
		debouncedTrack(query);
	};

	// Memoized debounced function
	const debouncedTrack = useCallback(
		debounce((query) => {
			if (query.trim()) {
				trackEvent(TRACKING_EVENT_NAMES.PIP_SEARCH, { query: query });
			}
		}, 800), // delay in ms
		[]
	);

	useEffect(() => {
		if (mode === MODES.CHAT) {
			const chatBody = document.querySelector(".chat-body-scrollable");
			if (chatBody) {
				chatBody.scrollTop = chatBody.scrollHeight;
			}
		}
	}, [chatHistory]);

	return (
		<div>
			<div className={`search-modal-overlay ${open ? "visible" : "hidden"}`} onClick={handleOverlayClick}>
				<div ref={modalRef} className={`search-modal-container ${mode === MODES.CHAT ? "chat-position" : ""}`}>
					{isFirstInteraction && (
						<div className="search-modal-welcome">
							<div className="welcome-content">
								<button className="welcome-close" onClick={handleWelcomeModalDismiss}>
									<ButtonIcon icon="close" />
								</button>
								<h2>Welcome to Atlas Spotlight!</h2>
								<p>
									From here, you can do anything{" "}
									<span role="img" aria-label="sparkles">
										✨
									</span>
								</p>
								<div className="welcome-actions">
									<button className="welcome-button" onClick={handleWelcomeModalDismiss}>
										Got it!
									</button>
									<span className="welcome-hint">Hit enter</span>
								</div>
							</div>
						</div>
					)}

					<div className="search-modal-input-wrapper" ref={inputFocusedRef}>
						<input
							autoFocus
							ref={inputRef}
							placeholder={placeholder}
							value={query}
							onChange={(e) => handleQueryInput(e.target.value)}
							className="search-modal-input"
						/>
						{/* <span className="search-icon">
						<ButtonIcon icon="search-lens" color="#d2d2d2" />
					</span> */}
						{/* <div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", right: "20px" }}>
						<VoiceSearch ref={voiceSearchRef} onSearch={handleVoiceSearch} />
					</div> */}
					</div>

					{isFirstInteraction && (
						<div className="search-modal-first-interaction" ref={introRef}>
							<h3>Just type what you need!</h3>
							<p className="subtitle">
								For eg., type a word like "Items" and see everything related in one place
							</p>

							<div className="search-modal-first-interaction-sections">
								<div className="section">
									<h3>Jump to a page</h3>
									<p>Quickly navigate to where you manage all your Items.</p>
								</div>

								<div className="section">
									<h3>See Analytics</h3>
									<p>Get an instant snapshot of how your Items are performing.</p>
								</div>

								<div className="section">
									<h3>Find help</h3>
									<p>Open guides and articles to solve Item-related questions.</p>
								</div>
							</div>
						</div>
					)}
					{!isFirstInteraction && (
						<div>
							{mode === MODES.SEARCH && (
								<div className="search-modal-results" ref={resultsRef}>
									<div>
										{query === "" && historyItems && historyItems.length > 0 && (
											<div className="search-modal-history">
												<div className="pip-recent-heading">Recents</div>
												{historyItems.map((item, index) => (
													<div
														id={`search-result-${index}`}
														key={item.id || index}
														className={`search-modal-result-item ${
															highlightedIndex === index ? "highlighted" : ""
														}`}
														onClick={() => handleItemClick(item)}
														onMouseEnter={() => scrollSelectedIntoViewOnHover(index)}
													>
														<div className="search-modal-result-content">
															<span className="search-modal-result-history-icon">
																<ButtonIcon icon="history" />
															</span>
															<span
																className={`search-modal-result-selected-type ${
																	highlightedIndex === index ? "highlighted" : ""
																}`}
															>
																{item.type}
															</span>
															{item.parentLabel && (
																<span className="search-modal-result-selected-category">
																	{item.parentLabel} {" / "}
																	<span
																		className="search-modal-result-selected-title"
																		title={item.title}
																	>
																		{item.title}
																	</span>
																</span>
															)}
															{!item.parentLabel && (
																<span
																	className="search-modal-result-selected-title-without-category"
																	title={item.title}
																>
																	{item.title}
																</span>
															)}
														</div>
														{highlightedIndex === index && (
															<div className="search-modal-result-content">
																<span className="search-modal-result-selected-icon">
																	<ButtonIcon icon="enter" />
																</span>
																<span className="search-modal-result-selected-text">
																	Enter to Select
																</span>
															</div>
														)}
													</div>
												))}
												<div className="pip-history-divider">
													<span className="pip-divider" />
												</div>
											</div>
										)}

										{filteredResults && filteredResults.length > 0 ? (
											<div className="category-items">
												{filteredResults.map((result, index) => {
													// Calculate the global index consistently
													const historyLength = query === "" ? historyItems?.length || 0 : 0;
													const globalIndex = index + historyLength;

													return (
														<div
															id={`search-result-${globalIndex}`}
															key={result.id}
															className={`search-modal-result-item ${
																highlightedIndex === globalIndex ? "highlighted" : ""
															} ${result.isMagical ? "magical" : ""}`}
															onClick={() => handleItemClick(result)}
															onMouseEnter={() =>
																scrollSelectedIntoViewOnHover(globalIndex)
															}
														>
															<div className="search-modal-result-content">
																<span
																	className={`search-modal-result-selected-type ${
																		highlightedIndex === globalIndex
																			? "highlighted"
																			: ""
																	}`}
																>
																	{result.type}
																</span>
																{result.parentLabel && (
																	<span className="search-modal-result-selected-category">
																		{result.parentLabel} {" / "}
																		<span
																			className="search-modal-result-selected-title"
																			title={result.title}
																		>
																			{result.title}
																		</span>
																	</span>
																)}
																{!result.parentLabel && (
																	<span
																		className="search-modal-result-selected-title-without-category"
																		title={result.title}
																	>
																		{result.title}
																	</span>
																)}
															</div>
															{highlightedIndex === globalIndex && (
																<div className="search-modal-result-content">
																	<span className="search-modal-result-selected-icon">
																		<ButtonIcon icon="enter" />
																	</span>
																	<span className="search-modal-result-selected-text">
																		Enter to Select
																	</span>
																</div>
															)}
														</div>
													);
												})}
											</div>
										) : (
											<div className="no-items-placeholder search-modal-empty">
												<div className="search-modal-empty">
													No results for "<strong className="no-result-query">{query}</strong>
													"
												</div>
												<div className="search-modal-empty-suggestions">
													You may want to try different keywords or check for any possible
													typos
												</div>
											</div>
										)}
									</div>
								</div>
							)}

							{mode === MODES.CHAT && (
								<>
									<div className="chat-header">
										<button
											className="chat-back-button"
											onClick={() => {
												setMode(MODES.SEARCH);
												setChatHistory([]);
												setQuery("");
											}}
										>
											← Back to Search
										</button>
									</div>
									<div className="chat-body-scrollable">
										{chatHistory.map((msg, idx) => (
											<div key={idx} className={`chat-bubble ${msg.type}`}>
												{msg.text}
											</div>
										))}
										{isLoading && (
											<div className="chat-bubble ai loading">
												<span className="loading-dots">Thinking</span>
											</div>
										)}
									</div>
								</>
							)}
						</div>
					)}
					<div className="search-modal-footer">
						<div className="search-modal-shortcuts">
							<div>
								<span className="pip-footer-icon">
									<ButtonIcon icon="up-down" />
								</span>
								Navigate
							</div>
							<div>
								<span className="pip-footer-icon">
									<ButtonIcon icon="enter" />
								</span>
								Select
							</div>
							<div>
								<span className="pip-footer-icon">ESC</span> Close
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
const mapStateToProps = (store) => ({
	pipSearchState: store.pipSearchState
});
export default connect(mapStateToProps)(PipSearch);
